import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

import { GlobalState } from "../../../../context/GlobalContext";
import { useBpV2State } from "../../../../context/BusinessPlanV2Context";

import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import CloseIcon from "@mui/icons-material/Close";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { DataGrid } from "@mui/x-data-grid";
import { grey } from "@mui/material/colors";

import { API, round1dp } from "../../data";
import { mrpGridColumns, mrpHiddenColumns, sortMrpRows } from "./data";
import parseErrMsgFromError from "utils/errorFunctions/parseErrorMessageFromError";
import "../../businessPlanV2.css";

const modalStyle = {
	position: "absolute",
	top: "50%",
	left: "50%",
	transform: "translate(-50%, -50%)",
	width: "90vw",
	maxWidth: 1200,
	height: "85vh",
	maxHeight: "85vh",
	bgcolor: "background.paper",
	borderRadius: 3,
	boxShadow: "0 20px 60px rgba(15, 23, 42, 0.25)",
	border: `1px solid ${grey[200]}`,
	px: 2.5,
	py: 1.5,
	overflow: "hidden",
	display: "flex",
	flexDirection: "column",
};

const computeRowPreview = (row) => {
	const qty = row.new_qty || 0;
	const mrp = row.mrp || 0;
	const dis = row.new_dis || 0;
	const nsv = qty * mrp - qty * mrp * (dis / 100);
	const asp = qty ? round1dp(nsv / qty) : 0;

	return {
		...row,
		new_nsv: round1dp(nsv),
		new_asp: asp,
		new_total_mrp: qty * mrp,
	};
};

const weightedDis = (totalMrp, nsv) => {
	if (!totalMrp) return 0;
	return round1dp(((totalMrp - nsv) / totalMrp) * 100);
};

const buildTotalRow = (rows) => {
	let ltlQty = 0;
	let newQty = 0;
	let ltlOpt = 0;
	let newOpt = 0;
	let ltlNsv = 0;
	let newNsv = 0;
	let ltlTotalMrp = 0;
	let newTotalMrp = 0;
	let ltlStWeighted = 0;
	let ltlDispatchQty = 0;

	rows.forEach((r) => {
		const lq = r.ltl_qty || 0;
		const nq = r.new_qty || 0;
		ltlQty += lq;
		newQty += nq;
		ltlOpt += r.ltl_opt || 0;
		newOpt += r.new_opt || 0;
		ltlNsv += r.ltl_nsv || 0;
		newNsv += r.new_nsv || 0;
		ltlTotalMrp += r.ltl_total_mrp || 0;
		newTotalMrp += r.new_total_mrp ?? nq * (r.mrp || 0);
		ltlStWeighted += lq * (r.ltl_st || 0);
		ltlDispatchQty += r.ltl_inventory_qty || 0;
	});

	return {
		id: "TOTAL",
		isTotal: true,
		sub_brand: "TOTAL",
		ltl_qty: ltlQty,
		new_qty: newQty,
		ltl_dis: weightedDis(ltlTotalMrp, ltlNsv),
		new_dis: weightedDis(newTotalMrp, newNsv),
		ltl_st: ltlQty ? round1dp(ltlStWeighted / ltlQty) : 0,
		ltl_inventory_qty: ltlDispatchQty,
		ltl_opt: ltlOpt,
		new_opt: newOpt,
		new_asp: newQty ? round1dp(newNsv / newQty) : 0,
		new_nsv: round1dp(newNsv),
	};
};

const MrpGrid = ({ open, onClose, submissionId, mrpRows, subCategory, category, division, canEdit, onSaved }) => {
	const { setLoadingOpen, setSnackBar, snackBar, setSnackBarOpen } = GlobalState();
	const { selectedStore, selectedPlan } = useBpV2State();

	const [rows, setRows] = useState([]);
	const [changedIds, setChangedIds] = useState(new Set());
	const [newMrp, setNewMrp] = useState("");
	const [newAttrValue, setNewAttrValue] = useState("");
	const [newSubBrand, setNewSubBrand] = useState("");
	const [columnVisibilityModel, setColumnVisibilityModel] = useState(mrpHiddenColumns);

	const planSeason = selectedPlan?.name || [selectedPlan?.season, selectedPlan?.year].filter(Boolean).join("-") || "—";
	const brand = selectedStore?.brand || "—";
	const crumbCategory = category || mrpRows?.[0]?.category || "—";
	const crumbSubCategory = subCategory || mrpRows?.[0]?.sub_category || "—";

	const displayRows = useMemo(() => {
		if (!rows.length) return [];
		return [buildTotalRow(rows), ...rows];
	}, [rows]);

	const showSnack = (message, severity = "error") => {
		setSnackBarOpen(true);
		setSnackBar({ ...snackBar, message, severity });
	};

	useEffect(() => {
		if (!open) return;
		setRows(sortMrpRows((mrpRows || []).map((r) => ({ ...r }))));
		setChangedIds(new Set());
	}, [mrpRows, open]);

	const processRowUpdate = (newRow) => {
		if (newRow.isTotal) return newRow;
		const updatedRow = computeRowPreview(newRow);
		setRows((prev) => prev.map((row) => (row.id === updatedRow.id ? updatedRow : row)));
		setChangedIds((prev) => new Set([...prev, updatedRow.id]));
		return updatedRow;
	};

	const handleSave = async () => {
		if (!changedIds.size) return;

		const updatedMrps = rows
			.filter((r) => changedIds.has(r.id))
			.map((r) => ({
				id: r.id,
				new_qty: r.new_qty || 0,
				new_dis: r.new_dis || 0,
				new_opt: r.new_opt || 0,
			}));

		try {
			setLoadingOpen(true);
			const { data } = await axios.put(API.BULK_UPDATE_MRP, {
				submission_id: submissionId,
				updatedMrps,
			});
			if (data.status === "success") {
				showSnack("Saved", "success");
				setChangedIds(new Set());
				onSaved?.();
				onClose();
			}
		} catch (err) {
			showSnack(parseErrMsgFromError(err));
		} finally {
			setLoadingOpen(false);
		}
	};

	const handleAddRow = async () => {
		if (!newMrp) return;
		try {
			setLoadingOpen(true);
			const { data } = await axios.post(API.ADD_MRP_ROW, {
				submission_id: submissionId,
				mrp: parseInt(newMrp),
				attribute_value: newAttrValue || "NA",
				sub_brand: newSubBrand || undefined,
				sub_category: subCategory,
				category,
				division,
			});
			if (data.status === "success") {
				showSnack("Row added", "success");
				setNewMrp("");
				setNewAttrValue("");
				setNewSubBrand("");
				onSaved?.();
			}
		} catch (err) {
			showSnack(parseErrMsgFromError(err));
		} finally {
			setLoadingOpen(false);
		}
	};

	const handleCancel = () => {
		setRows(sortMrpRows(mrpRows.map((r) => ({ ...r }))));
		setChangedIds(new Set());
	};

	const isCellEditable = (params) => {
		if (!canEdit || params.row.isTotal || params.row.disabled) return false;
		return true;
	};

	const getCellClassName = (params) => {
		if (params.row.isTotal || params.row.disabled) return "";
		const editableFields = ["new_qty", "new_dis", "new_opt"];
		if (canEdit && editableFields.includes(params.field)) return "cell-editable";
		return "cell-readonly";
	};

	const getRowClassName = (params) => {
		if (params.row.isTotal) return "row-total";
		if (params.row.disabled) return "row-disabled";
		return "";
	};

	const columns = mrpGridColumns(canEdit);

	return (
		<Modal open={open} onClose={onClose}>
			<Box sx={modalStyle}>
				<Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1.5} flexShrink={0}>
					<Stack spacing={0.5}>
						<Typography variant="h6" fontWeight={700}>
							MRP Data
						</Typography>
						<Breadcrumbs
							separator={<NavigateNextIcon fontSize="small" />}
							sx={{ "& .MuiBreadcrumbs-li": { fontSize: 15 } }}
						>
							<Typography fontSize={15} fontWeight={700} color="text.secondary">
								{planSeason}
							</Typography>
							<Typography fontSize={15} fontWeight={700} color="text.secondary">
								{selectedStore?.store_name || "—"}
							</Typography>
							<Typography fontSize={15} fontWeight={700} color="text.secondary">
								{brand}
							</Typography>
							<Typography fontSize={15} fontWeight={700} color="text.secondary">
								{crumbCategory}
							</Typography>
							<Typography fontSize={15} fontWeight={700} color="text.primary">
								{crumbSubCategory}
							</Typography>
						</Breadcrumbs>
					</Stack>
					<IconButton onClick={onClose} size="small">
						<CloseIcon fontSize="small" />
					</IconButton>
				</Stack>

				<Box sx={{ flex: 1, minHeight: 0, mb: 1.5 }}>
					<DataGrid
						rows={displayRows}
						columns={columns}
						density="compact"
						disableRowSelectionOnClick
						showCellVerticalBorder
						showColumnVerticalBorder
						processRowUpdate={processRowUpdate}
						onProcessRowUpdateError={(err) => console.log(err)}
						isCellEditable={isCellEditable}
						getRowClassName={getRowClassName}
						getCellClassName={getCellClassName}
						columnVisibilityModel={columnVisibilityModel}
						onColumnVisibilityModelChange={setColumnVisibilityModel}
						sx={{
							height: "100%",
							borderRadius: "8px",
							boxShadow: `2px 2px 5px ${grey[200]}`,
							"& .MuiDataGrid-columnHeader": {
								borderRight: `1px solid ${grey[300]}`,
							},
							"& .MuiDataGrid-cell": {
								borderRight: `1px solid ${grey[300]}`,
							},
							"& .cell-readonly": {
								bgcolor: grey[100],
							},
							"& .cell-editable": {
								bgcolor: "#fff",
							},
							"& .row-total": {
								bgcolor: "#000",
								color: "#fff",
								fontWeight: 700,
							},
							"& .row-total .MuiDataGrid-cell": {
								bgcolor: "#000",
								color: "#fff",
								fontWeight: 700,
								borderRight: `1px solid ${grey[700]}`,
							},
							"& .row-disabled": {
								bgcolor: "#f5f5f5",
								color: "#bdbdbd",
							},
							"& .row-disabled .MuiDataGrid-cell": {
								bgcolor: "#f5f5f5",
								color: "#bdbdbd",
							},
						}}
					/>
				</Box>

				{canEdit && (
					<Stack
						direction="row"
						spacing={2}
						alignItems="center"
						justifyContent="space-between"
						flexShrink={0}
						sx={{ bgcolor: grey[50], border: `1px solid ${grey[200]}`, borderRadius: 2, p: 1 }}
					>
						<Stack direction="row" spacing={2} alignItems="center">
							<TextField
								size="small"
								label="Sub Brand"
								value={newSubBrand}
								onChange={(e) => setNewSubBrand(e.target.value)}
								sx={{ width: 140, bgcolor: "white" }}
							/>
							<TextField
								size="small"
								label="New MRP"
								type="number"
								value={newMrp}
								onChange={(e) => setNewMrp(e.target.value)}
								sx={{ width: 130, bgcolor: "white" }}
							/>
							<TextField
								size="small"
								label="Attribute Value"
								value={newAttrValue}
								onChange={(e) => setNewAttrValue(e.target.value)}
								sx={{ width: 170, bgcolor: "white" }}
							/>
							<Button variant="outlined" startIcon={<AddRoundedIcon />} onClick={handleAddRow}>
								Add Row
							</Button>
						</Stack>
						<Stack direction="row" spacing={2} alignItems="center">
							<Button variant="outlined" onClick={handleCancel} disabled={!changedIds.size}>
								Cancel Changes
							</Button>
							<Button variant="contained" onClick={handleSave} disabled={!changedIds.size}>
								Save ({changedIds.size} changed)
							</Button>
						</Stack>
					</Stack>
				)}
			</Box>
		</Modal>
	);
};

export default MrpGrid;
